import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, TextField, Button, Chip, IconButton, Menu, MenuItem,
  ListItemIcon, ListItemText, Snackbar, Alert, InputAdornment,
} from '@mui/material'
import {
  Add, Search, MoreVert, Edit, Block, CheckCircleOutlined, LockReset, VpnKey,
  LockOpen, Logout as LogoutIcon, Shield,
} from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listUsers, deactivateUser, reactivateUser, resetUserPassword, forcePasswordChange,
  unlockUser, closeUserSessions, type UserListItem,
} from '../../api/admin'
import { errorMessage } from '../../api/client'
import UserFormDialog from './UserFormDialog'
import EffectivePermissionsDialog from './EffectivePermissionsDialog'

export default function UsersPage() {
  const [search, setSearch] = useState('')
  const [formTarget, setFormTarget] = useState<UserListItem | null | 'new'>(null)
  const [permsTarget, setPermsTarget] = useState<UserListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: UserListItem } | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'users', search], queryFn: () => listUsers(search || undefined) })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] })

  const actionMutation = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: (_data, fn) => {
      void invalidate()
      setToast('Operațiune reușită.')
      void fn
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const closeMenu = () => setMenuAnchor(null)
  const run = (fn: () => Promise<unknown>) => { closeMenu(); actionMutation.mutate(fn) }

  const columns = useMemo<GridColDef<UserListItem>[]>(() => [
    {
      field: 'name', headerName: 'Nume', flex: 1.2, minWidth: 180,
      valueGetter: (_v, row) => `${row.firstName} ${row.lastName}`,
    },
    { field: 'email', headerName: 'Email', flex: 1.3, minWidth: 200 },
    {
      field: 'roles', headerName: 'Roluri', flex: 1.2, minWidth: 180, sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
          {p.row.roles.map((r) => <Chip key={r.id} label={r.name} size="small" variant="outlined" />)}
        </Box>
      ),
    },
    {
      field: 'workPoints', headerName: 'Punct de lucru', flex: 1, minWidth: 150, sortable: false,
      valueGetter: (_v, row) => row.workPoints.map((w) => w.name).join(', '),
    },
    {
      field: 'status', headerName: 'Status', width: 130, sortable: false,
      renderCell: (p) => {
        if (p.row.lockedUntil && new Date(p.row.lockedUntil) > new Date())
          return <Chip label="Blocat" size="small" color="error" variant="outlined" />
        if (!p.row.isActive) return <Chip label="Inactiv" size="small" variant="outlined" />
        if (p.row.mustChangePassword) return <Chip label="Invitat" size="small" color="info" variant="outlined" />
        return <Chip label="Activ" size="small" color="success" variant="outlined" />
      },
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Administrare</Typography>
        <Typography variant="inherit" color="text.primary">Utilizatori</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h2">Utilizatori</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă</Button>
      </Box>

      <TextField
        placeholder="Caută după nume, email sau utilizator…" size="small" sx={{ maxWidth: 360 }}
        value={search} onChange={(e) => setSearch(e.target.value)}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid
          rows={data ?? []} columns={columns} loading={isLoading} density="comfortable"
          disableRowSelectionOnClick getRowHeight={() => 'auto'}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>

      <UserFormDialog user={formTarget} onClose={() => setFormTarget(null)} />
      <EffectivePermissionsDialog user={permsTarget} onClose={() => setPermsTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="perms" onClick={() => { setPermsTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Shield fontSize="small" /></ListItemIcon><ListItemText>Permisiuni efective</ListItemText>
          </MenuItem>,
          menuAnchor.row.isActive ? (
            <MenuItem key="deactivate" onClick={() => run(() => deactivateUser(menuAnchor.row.id))}>
              <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem key="reactivate" onClick={() => run(() => reactivateUser(menuAnchor.row.id))}>
              <ListItemIcon><CheckCircleOutlined fontSize="small" /></ListItemIcon><ListItemText>Reactivare</ListItemText>
            </MenuItem>
          ),
          <MenuItem key="reset-pw" onClick={() => run(() => resetUserPassword(menuAnchor.row.id))}>
            <ListItemIcon><LockReset fontSize="small" /></ListItemIcon><ListItemText>Trimite link resetare parolă</ListItemText>
          </MenuItem>,
          <MenuItem key="force-pw" onClick={() => run(() => forcePasswordChange(menuAnchor.row.id))}>
            <ListItemIcon><VpnKey fontSize="small" /></ListItemIcon><ListItemText>Forțează schimbare parolă</ListItemText>
          </MenuItem>,
          ...(menuAnchor.row.lockedUntil ? [
            <MenuItem key="unlock" onClick={() => run(() => unlockUser(menuAnchor.row.id))}>
              <ListItemIcon><LockOpen fontSize="small" /></ListItemIcon><ListItemText>Deblocare cont</ListItemText>
            </MenuItem>,
          ] : []),
          <MenuItem key="close-sessions" onClick={() => run(() => closeUserSessions(menuAnchor.row.id))}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon><ListItemText>Închide sesiunile active</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
